<div class="flex-1 flex flex-col overflow-auto">
  <!-- Letters Content -->
  <main
    class="flex-1 overflow-y-auto p-6 ipad-scrollable relative"
    style="min-height: 0; -webkit-overflow-scrolling: touch"
  >
    <!-- Full Page Loader -->
    @if (pageLoading) {
    <div
      class="flex items-center justify-center bg-white bg-opacity-95 absolute inset-0 z-10"
      style="min-height: calc(100vh - 80px); backdrop-filter: blur(2px);"
    >
      <app-loader
        type="fullscreen"
        message="Loading letters..."
        size="large"
        ariaLabel="Loading letters page"
      >
      </app-loader>
    </div>
    }
    <!-- Content Area - Only show when not loading -->
    @if (!pageLoading) {
    <!-- Top Section: Responsive Layout -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between lg:flex-row lg:items-center lg:justify-between mb-6 gap-3"
    >
      <!-- Desktop/Tablet Title -->
      <h1 class="hidden md:block text-xl font-bold text-black-600">Letters</h1>

      <!-- MOBILE: Row 1 (Title + Filter + Sort) -->
      <div class="flex md:hidden flex-row items-center justify-between w-full">
        <!-- Left side -->
        <h1 class="text-xl font-bold text-black-600">Letters</h1>

        <!-- Right side -->
        <div class="flex flex-row items-center gap-2">
          <app-filter-tabs
            [tabs]="filterTabs"
            [activeTab]="activeFilterTab"
            (tabChange)="onFilterTabChange($event)"
          ></app-filter-tabs>

          <app-sort-dropdown
            [options]="sortOptions"
            [selectedOption]="selectedSortOption"
            [placeholder]="'Sort By:'"
            (sortChange)="onSortChange($event)"
          ></app-sort-dropdown>
        </div>
      </div>

      <!-- DESKTOP/TABLET: Right side inline -->
      <div class="hidden md:flex flex-wrap lg:flex-nowrap items-center gap-2">
        <app-filter-tabs
          [tabs]="filterTabs"
          [activeTab]="activeFilterTab"
          (tabChange)="onFilterTabChange($event)"
        ></app-filter-tabs>

        <div class="search-bar-wrapper">
          <app-search-bar
            [placeholder]="'Search'"
            [value]="searchTerm"
            (searchChange)="onSearchChange($event)"
          ></app-search-bar>
          <button
            *ngIf="searchTerm && searchTerm.trim().length > 0"
            type="button"
            class="search-clear-button"
            (click)="clearSearch()"
            aria-label="Clear search"
            title="Clear search"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        <app-sort-dropdown
          [options]="sortOptions"
          [selectedOption]="selectedSortOption"
          [placeholder]="'Sort By:'"
          (sortChange)="onSortChange($event)"
        ></app-sort-dropdown>

        @if (!isManager) {
        <button
          pButton
          type="button"
          [label]="isMobile ? '+Add' : 'Add New Letter'"
          class="add-new-btn"
          (click)="onUploadDocument()"
        ></button>
        }
      </div>

      <!-- MOBILE: Row 2 (Search + Add New) -->
      <div class="flex md:hidden items-center gap-2">
        <div class="search-bar-wrapper">
          <app-search-bar
            [placeholder]="'Search'"
            [value]="searchTerm"
            (searchChange)="onSearchChange($event)"
          ></app-search-bar>
          <button
            *ngIf="searchTerm && searchTerm.trim().length > 0"
            type="button"
            class="search-clear-button"
            (click)="clearSearch()"
            aria-label="Clear search"
            title="Clear search"
          >
            <i class="pi pi-times"></i>
          </button>
        </div>

        @if (!isManager) {
        <button
          pButton
          type="button"
          [label]="isMobile ? '+Add' : 'Add New Letter'"
          class="add-new-btn"
          (click)="onUploadDocument()"
        ></button>
        }
      </div>
    </div>

    <!-- DESKTOP (XL and larger) -->
    <div
      class="hidden xl:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
    >
      @if (loading) {
      <!-- Loading State -->
      <app-loader type="inline" message="Loading letters..." size="medium">
      </app-loader>
      } @else if (data && data.length === 0) {
      <!-- Empty State -->
      <div class="flex flex-col items-center justify-center p-12 text-center">
        <div class="text-gray-400 mb-4">
          <i class="pi pi-inbox text-4xl"></i>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No letters found</h3>
        <p class="text-gray-500">
          There are no letters matching your current filters.
        </p>
      </div>
      } @else {
      <app-table
        [data]="data"
        [columns]="columns"
        [context]="'letters'"
        [isArchivedView]="activeFilterTab === 'archive'"
        (rowClick)="onRowClick($event)"
        (comment)="onCommentClick($event)"
        (reply)="onReply($event)"
        (auditLog)="onAuditLog($event)"
        (approve)="onApprove($event)"
        (reject)="onReject($event)"
        (edit)="onEdit($event)"
        (documentNameClick)="onDocumentNameClick($event)"
      ></app-table>

      <!-- Pagination for Desktop -->
      <app-pagination
        [paginationInfo]="paginationInfo"
        (pageChange)="onPageChange($event)"
      ></app-pagination>
      }
    </div>

    <!-- MOBILE/iPad Pro -->
    <div class="xl:hidden">
      @if (loading) {
      <!-- Loading State for Mobile -->
      <app-loader type="card" message="Loading letters..." size="medium">
      </app-loader>
      } @else if (data && data.length === 0) {
      <!-- Empty State for Mobile -->
      <div
        class="flex flex-col items-center justify-center p-12 text-center bg-white rounded-xl shadow-sm border border-gray-100"
      >
        <div class="text-gray-400 mb-4">
          <i class="pi pi-inbox text-4xl"></i>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No letters found</h3>
        <p class="text-gray-500">
          There are no letters matching your current filters.
        </p>
      </div>
      } @else {
      <app-card-list
        [data]="data"
        [context]="'letters'"
        [isArchivedView]="activeFilterTab === 'archive'"
        (rowClick)="onRowClick($event)"
        (auditLog)="onAuditLog($event)"
      ></app-card-list>

      <!-- Pagination for Mobile -->
      <app-pagination
        [paginationInfo]="paginationInfo"
        (pageChange)="onPageChange($event)"
      ></app-pagination>
      }
    </div>
    }
  </main>

  <!-- Audit Log Modal -->
  <app-audit-modal
    [(visible)]="auditDialogVisible"
    [logs]="auditData"
    [row]="selectedRow"
    [loading]="auditLoading"
    (close)="onAuditClose()"
  ></app-audit-modal>

  <!-- Document Viewer Modal -->
  <app-document-viewer-modal
    [(visible)]="showDocumentViewer"
    [attachment]="currentDocument"
    [loading]="documentLoading"
    (visibleChange)="showDocumentViewer = $event"
    (download)="onDocumentDownload($event)"
    (openInNewTab)="onDocumentOpenInNewTab($event)"
    (close)="onDocumentViewerClose()"
  ></app-document-viewer-modal>

  <!-- Toast Messages -->
  <p-toast position="top-right"></p-toast>
</div>
